/**
 * 반 화면에서 빠르게 남기는 메모: 사실 태그, 날짜 칩, 붙여넣기 나누기.
 * 태그는 문장이 아니라 본 사실의 이름이다(생기부 문장은 나중에 근거만큼만 쓴다).
 */

export const QUICK_TAGS = [
  { label: '발표', area: '교과' },
  { label: '질문', area: '교과' },
  { label: '친구 도움', area: '행동' },
  { label: '모둠 정리', area: '교과' },
  { label: '학급 일', area: '자율' },
  { label: '지각', area: '행동' },
  { label: '과제 미제출', area: '교과' },
];

const pad = value => String(value).padStart(2, '0');

export const dayKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todayKey = (now = new Date()) => dayKey(now);

export const yesterdayKey = (now = new Date()) => {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  return dayKey(date);
};

/** 학년도(3월 시작) 첫날·마지막 날. 날짜 고르기 범위로 쓴다. */
export const schoolYearRange = (year) => {
  const last = new Date(Number(year) + 1, 2, 0);
  return { min: `${year}-03-01`, max: dayKey(last) };
};

export const daySchoolYear = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!match) return null;
  const year = Number(match[1]);
  return Number(match[2]) >= 3 ? year : year - 1;
};

/** 명단 학년도 안이면 오늘, 아니면 빈 값(선생님이 날짜를 골라야 한다). */
export const defaultMemoDay = (schoolYear, now = new Date()) => {
  const today = todayKey(now);
  return !schoolYear || daySchoolYear(today) === Number(schoolYear) ? today : '';
};

export const formatShortDay = (key) => {
  const [, month, day] = String(key || '').split('-').map(Number);
  return month && day ? `${month}월 ${day}일` : '';
};

/** 태그와 한 줄 입력을 메모 글로. 태그만 눌러도 된다. */
export const quickMemoText = (tag, text) => [tag, String(text || '').trim()].filter(Boolean).join(' · ');

/** 입력 글로 영역을 정할 때 태그가 있으면 태그 영역이 먼저다. */
export const quickMemoArea = (tag, suggested = '') => QUICK_TAGS.find(item => item.label === tag)?.area || suggested || '';

const DATE_PATTERNS = [
  [/^(\d{4})-(\d{1,2})-(\d{1,2})\s*/, m => [Number(m[1]), Number(m[2]), Number(m[3])]],
  [/^(\d{1,2})\s*월\s*(\d{1,2})\s*일\s*/, m => [null, Number(m[1]), Number(m[2])]],
  [/^(\d{1,2})[./](\d{1,2})\.?(?=\s|$)\s*/, m => [null, Number(m[1]), Number(m[2])]],
];

const leadingDate = (line, schoolYear) => {
  for (const [pattern, pick] of DATE_PATTERNS) {
    const match = pattern.exec(line);
    if (!match) continue;
    const [givenYear, month, day] = pick(match);
    if (!(month >= 1 && month <= 12 && day >= 1 && day <= 31)) return { rest: line.slice(match[0].length), date: '', bad: true };
    const year = givenYear || (schoolYear ? (month >= 3 ? Number(schoolYear) : Number(schoolYear) + 1) : new Date().getFullYear());
    const date = new Date(year, month - 1, day);
    if (date.getMonth() !== month - 1) return { rest: line.slice(match[0].length), date: '', bad: true };
    return { rest: line.slice(match[0].length), date: dayKey(date), bad: false };
  }
  return { rest: line, date: '', bad: false };
};

const SEPARATORS = /^[\s:：\-–—·,.)]+/;

/**
 * 붙여넣은 여러 줄을 학생별 메모로 나눈다. AI 없이 번호·이름으로만 맞춘다.
 * 한 줄: [날짜] [번호] [이름] 내용 — 예: "3 김철수 발표 때 근거 제시", "10/2 김철수: 모둠 정리".
 * issue: '' | 'no_student'(못 찾음) | 'mismatch'(번호와 이름이 다른 학생) | 'ambiguous'(같은 이름) | 'no_text' | 'bad_date' | 'out_of_year' | 'future'
 */
export const parsePastedMemos = (text, students = [], { defaultDate = '', schoolYear = null } = {}) => {
  const byNumber = new Map(students.map(student => [Number(student.number), student]));
  const names = students
    .flatMap(student => [student.name, ...(student.aliases || [])]
      .map(name => String(name || '').trim())
      .filter(name => name.length >= 2)
      .map(name => ({ name, student })))
    .sort((a, b) => b.name.length - a.name.length);

  return String(text || '')
    .split(/\r?\n/)
    .map(raw => raw.replace(/\t+/g, ' ').trim())
    .filter(Boolean)
    .map((line) => {
      const { rest: afterDate, date, bad } = leadingDate(line, schoolYear);
      let rest = afterDate;
      let number = null;
      const numberMatch = /^(\d{1,2})\s*(?:번)?(?:[.):,]|\s)\s*/.exec(rest);
      if (numberMatch) {
        number = Number(numberMatch[1]);
        rest = rest.slice(numberMatch[0].length);
      }
      const nameHit = names.find(({ name }) => rest.startsWith(name) && (rest.length === name.length || /^[\s:：\-–—·,]/.test(rest.slice(name.length))));
      const sameName = nameHit ? names.filter(item => item.name === nameHit.name && item.student.sid !== nameHit.student.sid) : [];
      if (nameHit) rest = rest.slice(nameHit.name.length);
      const body = rest.replace(SEPARATORS, '').trim();

      const byNo = number !== null ? byNumber.get(number) || null : null;
      let student = null;
      let issue = '';
      if (nameHit) {
        const candidates = [nameHit.student, ...sameName.map(item => item.student)];
        if (byNo) {
          if (candidates.some(candidate => candidate.sid === byNo.sid)) student = byNo;
          else issue = 'mismatch';
        } else if (candidates.length > 1) issue = 'ambiguous';
        else student = nameHit.student;
      } else if (byNo) student = byNo;
      else issue = 'no_student';
      const memoDate = date || defaultDate;
      let dateIssue = '';
      if (bad || !memoDate) dateIssue = 'bad_date';
      else if (schoolYear && daySchoolYear(memoDate) !== Number(schoolYear)) dateIssue = 'out_of_year';
      else if (memoDate > todayKey()) dateIssue = 'future';
      if (!issue && !body) issue = 'no_text';
      if (!issue) issue = dateIssue;
      return { line, sid: issue ? null : student.sid, student: issue ? null : student, text: body, date: memoDate, issue, dateIssue };
    });
};

export const PASTE_ISSUE_LABELS = {
  no_student: '학생을 못 찾았어요',
  mismatch: '번호와 이름이 달라요',
  ambiguous: '같은 이름이 있어요 · 번호를 붙여 주세요',
  no_text: '내용이 없어요',
  bad_date: '날짜를 확인해 주세요',
  out_of_year: '명단 학년도 밖 날짜예요',
  future: '앞으로의 날짜예요',
};

const hash53 = (text, seed) => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
};

/**
 * 붙여넣기 줄의 client_id. 같은 명단·학생·날짜·내용(같은 줄이면 몇 번째인지)이면 늘 같은 값이라,
 * 시트를 닫았다 다시 붙여도 서버가 같은 문서로 받는다. 서버 허용: [A-Za-z0-9_-]{8,64}.
 */
export const pasteClientId = ({ rosterId, sid, date, text, nth = 1 }) => {
  const key = [rosterId, sid, date, String(text || '').replace(/\s+/g, ' ').trim(), nth].join('|');
  return `p${hash53(key, 1)}${hash53(key, 7)}`;
};
